import React, { useContext } from 'react';
import _ from 'lodash';
import { styled } from '@this/constants/themes';
import Typography from '@material-ui/core/Typography';
import MomentUtils from '@date-io/moment';
import Button from '@material-ui/core/Button';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import type { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import type Adjustment from '@this/domain/appoint/adjustment';
import type Candidate from '@this/domain/appoint/candidate';
import muiTheme from '@this/components/appoint/mui_theme';
import AppointContext from '@this/components/appoint/appoint_context';
import { CandidatesButtonBox } from '../adjustments/show/show';

interface Props {
  adjustment: Adjustment | null;
  candidateSlots: Candidate[];
  selectedCandidate: Candidate | null;
  setSelectedCandidate: (candidate: Candidate | null) => void;
  selectedDate: MaterialUiPickersDate;
  setSelectedDate: (date: MaterialUiPickersDate) => void;
  toggleSelectedCandidate: (candidate: Candidate) => void;
}

const DashboardCandidateArea = (props: Props) => {
  const contextValue = useContext(AppointContext);
  const adjustment = props.adjustment;
  const candidateSlots = props.candidateSlots;
  const selectedCandidate = props.selectedCandidate;
  const setSelectedCandidate = props.setSelectedCandidate;
  const selectedDate = props.selectedDate;
  const setSelectedDate = props.setSelectedDate;
  const toggleSelectedCandidate = props.toggleSelectedCandidate;

  const shouldDisableDate = (d: MaterialUiPickersDate) => {
    if (d && adjustment && adjustment.candidates.length > 0) {
      const selectableDates = adjustment.candidates.map(c => c.start.format('YYYY-MM-DD'));
      return selectableDates.indexOf(d.format('YYYY-MM-DD')) === -1;
    }
    return true;
  };

  const handleSetSelectedDate = (d: MaterialUiPickersDate) => {
    const newDate = d ? d.format('YYYY-MM-DD') : null;
    const oldDate = selectedDate ? selectedDate.format('YYYY-MM-DD') : null;
    const candidateDate = selectedCandidate ? selectedCandidate.start.format('YYYY-MM-DD') : null;

    if (oldDate && newDate !== oldDate) {
      if (candidateDate && candidateDate !== newDate) {
        setSelectedCandidate(null);
      }
      setSelectedDate(d);
    }
  };

  return (
    <CandidatesArea>
      {candidateSlots.length > 0 && (
        <CandidatesBox isMobile={contextValue.isMobile}>
          <CandidatesCalendarBox isMobile={contextValue.isMobile}>
            <MuiPickersUtilsProvider utils={MomentUtils}>
              <DatePicker
                onChange={(d: MaterialUiPickersDate) => handleSetSelectedDate(d)}
                shouldDisableDate={(d: MaterialUiPickersDate) => shouldDisableDate(d)}
                value={selectedDate}
                disableToolbar
                fullWidth
                variant="static"
                disablePast
              />
            </MuiPickersUtilsProvider>
          </CandidatesCalendarBox>
          <CandidatesSelectorBox>
            <Typography style={{ width: '100%', padding: '9px 0 6px', fontSize: '12px', lineHeight: '20px' }}>
              {`
                ${candidateSlots[0].start.format('M月D日 (ddd) ')}
                ${adjustment?.duration}分
              `}
            </Typography>
            {candidateSlots.map(c => (
              <CandidatesButtonBox key={c.start.format()}>
                <Button
                  variant={selectedCandidate && selectedCandidate.start.isSame(c.start) ? 'contained' : 'outlined'}
                  color="primary"
                  fullWidth
                  onClick={() => toggleSelectedCandidate(c)}
                >
                  {c.start.format('HH:mm 〜')}
                </Button>
              </CandidatesButtonBox>
            ))}
          </CandidatesSelectorBox>
        </CandidatesBox>
      )}
    </CandidatesArea>
  );
};

const CandidatesArea = styled.div`
  margin-bottom: 30px;
`;

const CandidatesBox = styled.div<{ isMobile: boolean }>`
  display: flex;
  width: min-content;
  padding: 0 54px 0 24px;

  ${props =>
    props.isMobile &&
    `
    flex-direction: column;
    align-items: center;
    box-sizing: border-box;
    width: 100%;
    padding: 0 16px;
  `}
`;

const CandidatesCalendarBox = styled.div<{ isMobile: boolean }>`
  margin-right: 20px;
  padding-right: 20px;
  border-right: solid 1px #ededed;

  .MuiPickersStaticWrapper-staticWrapperRoot {
    min-width: 196px;
  }

  .MuiPickersCalendarHeader-switchHeader {
    margin: 4px 0;

    .MuiIconButton-root {
      padding: 4px;

      svg {
        font-size: 20px;
      }
    }

    .MuiPickersCalendarHeader-transitionContainer {
      order: -1;

      .MuiTypography-alignCenter {
        padding-left: 10px;
        font-size: 12px;
        text-align: left;
      }
    }
  }

  .MuiPickersBasePicker-pickerView {
    max-width: none;
    min-width: 196px;
    min-height: 204px;
    height: 204px;

    .MuiPickersCalendar-transitionContainer {
      min-height: 140px;
      height: 140px;
    }
  }

  .MuiPickersCalendarHeader-dayLabel {
    width: 28px;
    margin: 0;
    color: inherit;
    font-size: 10px;
  }

  .MuiPickersSlideTransition-transitionContainer > * {
    position: relative;
  }

  .MuiPickersDay-day {
    width: 28px;
    height: 28px;
    margin: 0;
    background: ${muiTheme.palette.primary.light};

    &.MuiPickersDay-current {
      color: black;
      background: #fff;
      border: solid 1px ${muiTheme.palette.primary.main};
    }

    &.MuiPickersDay-daySelected {
      background: ${muiTheme.palette.primary.main};
    }

    &.MuiPickersDay-dayDisabled {
      color: black;
      background: #fff;
    }

    .MuiTypography-body2 {
      font-size: 10px;
    }
  }

  ${props =>
    props.isMobile &&
    `
    margin: 0 0 20px;
    padding-right: 0;
    border-right: 0;
  `}
`;

const CandidatesSelectorBox = styled.div`
  display: flex;
  flex-wrap: wrap;
  place-content: flex-start;
  justify-content: space-between;
  width: 268px;
`;

export default DashboardCandidateArea;
